/**
 * Chat Message Component
 * Displays individual messages in the chat
 */
import React from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import { UserOutlined, RobotOutlined, InfoCircleOutlined, ArrowRightOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';

const MessageContainer = styled.div<{ $type: string }>`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  
  ${props => props.$type === 'user' && `
    flex-direction: row-reverse;
  `}
`;

const Avatar = styled.div<{ $type: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  ${props => {
    switch (props.$type) {
      case 'user':
        return `
          background: var(--color-primary);
          color: white;
        `;
      case 'agent':
        return `
          background: var(--color-success-light);
          color: var(--color-success-dark);
        `;
      case 'system':
        return `
          background: var(--color-gray-200);
          color: var(--color-gray-600);
        `;
    }
  }}
`;

const MessageBubble = styled.div<{ $type: string; $error?: boolean }>`
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 12px;
  
  ${props => {
    if (props.$error) {
      return `
        background: var(--color-danger-light);
        color: var(--color-danger-dark);
      `;
    }
    
    switch (props.$type) {
      case 'user':
        return `
          background: var(--color-primary);
          color: white;
        `;
      case 'agent':
        return `
          background: var(--color-gray-100);
          color: var(--text-primary);
        `;
      case 'system':
        return `
          background: var(--color-info-light);
          color: var(--color-info-dark);
        `;
    }
  }}
`;

const Timestamp = styled.div`
  font-size: 11px;
  color: var(--text-tertiary);
  margin-top: 4px;
`;

const MarkdownContent = styled.div`
  /* Ensure proper list formatting */
  ul, ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }
  
  li {
    margin: 0.25em 0;
  }
  
  /* Paragraph spacing */
  p {
    margin: 0.5em 0;
    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* Headers */
  h1, h2, h3, h4, h5, h6 {
    margin: 0.75em 0 0.5em 0;
    font-weight: 600;
    &:first-child {
      margin-top: 0;
    }
  }
  
  /* Code blocks */
  code {
    background: rgba(0, 0, 0, 0.05);
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-size: 0.9em;
  }
  
  pre {
    background: rgba(0, 0, 0, 0.05);
    padding: 0.75em;
    border-radius: 4px;
    overflow-x: auto;
  }
  
  /* Strong text */
  strong {
    font-weight: 600;
  }
`;

const ActionButtonContainer = styled.div`
  margin-top: 12px;
`;

interface ChatMessageProps {
  message: {
    type: 'user' | 'agent' | 'system';
    content: string;
    timestamp: Date;
    error?: boolean;
    pendingAction?: any;
  };
  onProceed?: (action: any) => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, onProceed }) => {
  const getIcon = () => {
    switch (message.type) {
      case 'user':
        return <UserOutlined />;
      case 'agent':
        return <RobotOutlined />;
      case 'system':
        return <InfoCircleOutlined />;
    }
  };

  return (
    <MessageContainer $type={message.type}>
      <Avatar $type={message.type}>
        {getIcon()}
      </Avatar>
      <div>
        <MessageBubble $type={message.type} $error={message.error}>
          <MarkdownContent>
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </MarkdownContent>
          {message.pendingAction && onProceed && (
            <ActionButtonContainer>
              <Button
                type="primary"
                icon={<ArrowRightOutlined />}
                onClick={() => onProceed(message.pendingAction)}
              >
                Proceed
              </Button>
            </ActionButtonContainer>
          )}
        </MessageBubble>
        <Timestamp>
          {message.timestamp.toLocaleTimeString()}
        </Timestamp>
      </div>
    </MessageContainer>
  );
};